import axios from 'axios';
import { supabase } from '@/core/supabase';
import {
    createCreditApiScenario,
    EXECUTE_STATUS,
    VERIFY_STATUS
} from '@/core/credit/mocks';
import {
    createNormalizedError,
    normalizeExecuteResponse,
    normalizeVerifyResponse
} from '@/core/api/creditAdapter';
import { creditApiRuntime } from '@/config/featureFlags';

const API_BASE_URL = creditApiRuntime.baseURL;
const REQUEST_TIMEOUT_MS = 10000;

function isMockModeEnabled() {
    return Boolean(creditApiRuntime.mockMode);
}

async function getAuthHeaders() {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) {
        return {};
    }
    return {
        Authorization: `Bearer ${session.access_token}`
    };
}

function getMockScenario() {
    const scenarioName = creditApiRuntime.mockScenario || 'success';
    return createCreditApiScenario({ scenarioName });
}

const creditService = {
    async executeCreditAction(payload, options = {}) {
        const { timeoutMs = REQUEST_TIMEOUT_MS } = options;
        const mockMode = isMockModeEnabled();

        console.info('[credit-service] execute start', {
            requestId: payload?.requestId,
            actionType: payload?.actionType,
            featureCode: payload?.featureCode,
            subFeatureCode: payload?.subFeatureCode,
            mockMode,
            scenario: creditApiRuntime.mockScenario
        });

        if (mockMode) {
            const scenario = getMockScenario();
            const executeStatus = scenario.execute.status;
            if (executeStatus === EXECUTE_STATUS.SUCCESS) {
                console.info('[credit-service] execute mock success', {
                    requestId: payload?.requestId,
                    scenario: creditApiRuntime.mockScenario || 'success'
                });
                return {
                    success: true,
                    requestId: payload.requestId,
                    remainingCredits: 99,
                    unlockScope: payload.actionType === 'confirm_charge' ? 'result_scope' : 'none',
                    version: 'mock-v1',
                    raw: { mock: true, status: 'success' }
                };
            }
            if (executeStatus === EXECUTE_STATUS.INSUFFICIENT_CREDITS) {
                console.warn('[credit-service] execute mock insufficient credits', {
                    requestId: payload?.requestId
                });
                throw createNormalizedError('insufficient_credits', 'insufficient_credits', { mock: true });
            }
            if (executeStatus === EXECUTE_STATUS.TIMEOUT) {
                console.warn('[credit-service] execute mock timeout', {
                    requestId: payload?.requestId
                });
                throw createNormalizedError('credit_execute_timeout', 'credit_execute_timeout', { mock: true });
            }
            console.warn('[credit-service] execute mock failed', {
                requestId: payload?.requestId
            });
            throw createNormalizedError('credit_execute_failed', 'credit_execute_failed', { mock: true });
        }

        try {
            const headers = await getAuthHeaders();
            const response = await axios.post(
                `${API_BASE_URL}/execute/`,
                {
                    requestId: payload.requestId,
                    imageId: payload.imageId,
                    originalHashId: payload.originalHashId,
                    presetStyleId: payload.presetStyleId,
                    scopeType: payload.scopeType,
                    scopeKey: payload.scopeKey,
                    featureCode: payload.featureCode,
                    subFeatureCode: payload.subFeatureCode,
                    actionType: payload.actionType
                },
                {
                    headers: {
                        ...headers,
                        'Content-Type': 'application/json'
                    },
                    timeout: timeoutMs
                }
            );
            console.info('[credit-service] execute success', {
                requestId: payload?.requestId
            });
            return normalizeExecuteResponse(response.data);
        } catch (error) {
            if (error?.code === 'ECONNABORTED') {
                console.warn('[credit-service] execute timeout', {
                    requestId: payload?.requestId
                });
                throw createNormalizedError('credit_execute_timeout', 'credit_execute_timeout', { raw: error });
            }
            if (error?.code === 'insufficient_credits') {
                throw error;
            }
            console.warn('[credit-service] execute failed', {
                requestId: payload?.requestId,
                message: error?.message || error
            });
            throw createNormalizedError('credit_execute_failed', error?.message || 'credit_execute_failed', { raw: error });
        }
    },

    async verifyCreditAction(requestId) {
        if (!requestId) {
            throw createNormalizedError('credit_verify_invalid_request_id', 'credit_verify_invalid_request_id');
        }

        console.info('[credit-service] verify start', {
            requestId,
            mockMode: isMockModeEnabled()
        });

        if (isMockModeEnabled()) {
            const scenario = getMockScenario();
            const verifyStatus = scenario.verify.status;
            if (verifyStatus === VERIFY_STATUS.SUCCESS) {
                console.info('[credit-service] verify mock success', { requestId });
                return {
                    success: true,
                    verified: true,
                    requestId,
                    raw: { mock: true, status: 'success' }
                };
            }
            if (verifyStatus === VERIFY_STATUS.PENDING) {
                console.warn('[credit-service] verify mock pending', { requestId });
                return {
                    success: false,
                    verified: false,
                    pending: true,
                    requestId,
                    raw: { mock: true, status: 'pending' }
                };
            }
            console.warn('[credit-service] verify mock failed', { requestId });
            return {
                success: false,
                verified: false,
                pending: false,
                requestId,
                raw: { mock: true, status: 'failure' }
            };
        }

        try {
            const headers = await getAuthHeaders();
            const response = await axios.post(
                `${API_BASE_URL}/verify/`,
                { requestId },
                {
                    headers: {
                        ...headers,
                        'Content-Type': 'application/json'
                    },
                    timeout: REQUEST_TIMEOUT_MS
                }
            );
            console.info('[credit-service] verify done', {
                requestId,
                status: response?.data?.status || response?.data?.verifyStatus || null
            });
            return normalizeVerifyResponse(response.data);
        } catch (error) {
            console.warn('[credit-service] verify failed', {
                requestId,
                message: error?.message || error
            });
            throw createNormalizedError('credit_verify_failed', error?.message || 'credit_verify_failed', { raw: error });
        }
    }
};

export default creditService;
